/**
 * Lista contenziosi con filtri stato/tipo/anno.
 * Aggiornamento stato ed esito inline.
 */

import React, { useEffect, useState } from 'react';
import { useUrlState, useUrlNumberState } from '@lib/useUrlState';
import { Button, Card, Input } from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';

interface Contenzioso {
  id: string;
  ricezione_id: string;
  anno: number;
  tipo: string;
  stato: string;
  importo_contestato: string | null;
  valuta: string;
  note: string;
  esito: string | null;
  chiuso_at: string | null;
  created_at: string;
  updated_at: string;
}

const TIPI: Record<string, string> = {
  danno: 'Danno',
  mancanza: 'Mancanza',
  rifiuto_totale: 'Rifiuto totale',
  errore_fatturazione: 'Errore fatturazione',
};

const STATI_CONTENZIOSO: Record<string, { label: string; className: string }> = {
  aperto: { label: 'Aperto', className: 'bg-destructive/10 text-destructive' },
  in_gestione: { label: 'In gestione', className: 'bg-warning/10 text-warning' },
  risolto: { label: 'Risolto', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' },
  chiuso_senza_esito: { label: 'Chiuso s/e', className: 'bg-muted text-muted-foreground' },
};

export function ContenziosiList() {
  const { currency, date } = useFormatters();
  const token = localStorage.getItem('token');

  const [contenziosi, setContenziosi] = useState<Contenzioso[]>([]);
  const [loading, setLoading] = useState(true);
  const [annoFiltro, setAnnoFiltro] = useUrlNumberState('anno', new Date().getFullYear());
  const [statoFiltro, setStatoFiltro] = useUrlState('stato', '');
  const [tipoFiltro, setTipoFiltro] = useUrlState('tipo', '');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editStato, setEditStato] = useState('');
  const [editEsito, setEditEsito] = useState('');

  const fetchData = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('anno', String(annoFiltro));
    if (statoFiltro) params.set('stato', statoFiltro);
    if (tipoFiltro) params.set('tipo', tipoFiltro);

    const res = await fetch(`/api/v1/ordini/contenziosi?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) setContenziosi(await res.json());
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [annoFiltro, statoFiltro, tipoFiltro, token]);

  const startEdit = (c: Contenzioso) => {
    setEditingId(c.id);
    setEditStato(c.stato);
    setEditEsito(c.esito || '');
  };

  const salvaEdit = async (id: string) => {
    await fetch(`/api/v1/ordini/contenziosi/${id}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        stato: editStato,
        esito: editEsito || null,
      }),
    });
    setEditingId(null);
    await fetchData();
  };

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-outfit font-semibold text-foreground">
        Contenziosi
      </h1>

      <div className="flex gap-3">
        <select
          value={annoFiltro}
          onChange={(e) => setAnnoFiltro(Number(e.target.value))}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
        >
          {[2024, 2025, 2026, 2027].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <select
          value={statoFiltro}
          onChange={(e) => setStatoFiltro(e.target.value)}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
        >
          <option value="">Tutti gli stati</option>
          <option value="aperto">Aperto</option>
          <option value="in_gestione">In gestione</option>
          <option value="risolto">Risolto</option>
          <option value="chiuso_senza_esito">Chiuso s/e</option>
        </select>
        <select
          value={tipoFiltro}
          onChange={(e) => setTipoFiltro(e.target.value)}
          className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
        >
          <option value="">Tutti i tipi</option>
          <option value="danno">Danno</option>
          <option value="mancanza">Mancanza</option>
          <option value="rifiuto_totale">Rifiuto totale</option>
          <option value="errore_fatturazione">Errore fatturazione</option>
        </select>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Caricamento…</div>
        ) : contenziosi.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">Nessun contenzioso</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Data</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Tipo</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Stato</th>
                <th className="text-right px-4 py-2 font-medium text-muted-foreground">Importo</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Note</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Esito</th>
                <th className="text-left px-4 py-2 font-medium text-muted-foreground">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {contenziosi.map((c) => {
                const statoConfig = STATI_CONTENZIOSO[c.stato];
                const isEditing = editingId === c.id;

                return (
                  <tr key={c.id} className="border-b border-border">
                    <td className="px-4 py-2">{date(c.created_at)}</td>
                    <td className="px-4 py-2">{TIPI[c.tipo] || c.tipo}</td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <select
                          value={editStato}
                          onChange={(e) => setEditStato(e.target.value)}
                          className="rounded border border-border bg-card text-foreground px-2 py-1 text-xs"
                        >
                          <option value="aperto">Aperto</option>
                          <option value="in_gestione">In gestione</option>
                          <option value="risolto">Risolto</option>
                          <option value="chiuso_senza_esito">Chiuso s/e</option>
                        </select>
                      ) : (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statoConfig?.className || ''}`}>
                          {statoConfig?.label || c.stato}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {c.importo_contestato ? currency(c.importo_contestato) : '—'}
                    </td>
                    <td className="px-4 py-2 max-w-[200px] truncate" title={c.note}>
                      {c.note}
                    </td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <Input
                          value={editEsito}
                          onChange={(e: any) => setEditEsito(e.target.value)}
                          className="w-40"
                          placeholder="Esito…"
                        />
                      ) : (
                        c.esito || '—'
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <div className="flex gap-1">
                          <Button size="sm" onClick={() => salvaEdit(c.id)}>
                            Salva
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                            Annulla
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEdit(c)}
                        >
                          Modifica
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
